import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Button, Card, Col, Form, InputGroup, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import swal from "sweetalert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookOpen, faCalendarAlt, faExchangeAlt, faExternalLinkAlt, faPen, faPlus, faSyncAlt, faTrash } from "@fortawesome/free-solid-svg-icons";
import Layout from "../../../universal/layout/Layout";
import AlphaBanner from "app/orderTool/components/AlphaBanner";
import HelpTip from "app/orderTool/components/HelpTip";
import { EmptyState } from "app/orderTool/components/pageUi";
import { getProductPackages, getProductGroups, getProducts, getProductParts } from "state/adminData/actions";
import {
  getRotationSlots,
  postRotationSlot,
  putRotationSlot,
  deleteRotationSlot,
  postRotationSlotEntry,
  putRotationSlotEntry,
  deleteRotationSlotEntry,
  putRotationSlotPackage,
  deleteRotationSlotPackage,
  putRotationSlotGroup,
  deleteRotationSlotGroup,
  updateRotationPeriod,
} from "state/rotationSlots/actions";
import { resolvePackageForDeliveryDate, resolveSlotEntryOn, RESOLUTION_MODE_LABELS } from "helper/packageRotationHelper";
import CascadeModal from "./CascadeModal";
import CarteOverview from "./CarteOverview";
import ForfaitsAtDate from "./ForfaitsAtDate";
import PreparationCarte from "./PreparationCarte";
import { frDate, slotSummary, sonataGroupEditUrl, sonataPackageEditUrl, todayYmd } from "./slotLabels";

// Notice publique (guide utilisateur) servie par le BO Symfony
const NOTICE_URL = `${process.env.REACT_APP_API_URL}/notice/rotation-forfaits`;

/**
 * Rotation de la carte B2B par slots (forfaits, groupes de produits, produits — pôle production).
 *
 * Un forfait reste une entité avec un listing d'items, mais ce listing peut
 * contenir des lignes de slot : le produit concret est donné par le calendrier
 * du slot selon la date de livraison du devis. Une saisie calendrier sert tous
 * les forfaits qui portent le slot ; la cascade met à jour les devis déjà créés.
 */
const RotationsForfaits = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "Rotation de la carte — Le Moulin";
    return () => { document.title = prev; };
  }, []);

  const dispatch = useDispatch();
  const slotsState = useSelector((s) => s.rotationSlots);
  const packagesState = useSelector((s) => s.adminData.productPackages);
  const productGroupsState = useSelector((s) => s.adminData.productGroups);
  const productsState = useSelector((s) => s.adminData.products);
  const productPartsState = useSelector((s) => s.adminData.productParts);
  const slots = useMemo(() => slotsState?.data || [], [slotsState]);
  const packages = useMemo(() => packagesState?.data || [], [packagesState]);
  const productGroups = useMemo(() => productGroupsState?.data || [], [productGroupsState]);
  const products = useMemo(() => productsState?.data || [], [productsState]);
  const productParts = useMemo(() => productPartsState?.data || [], [productPartsState]);

  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [newSlotName, setNewSlotName] = useState("");
  const [newSlotType, setNewSlotType] = useState("product");
  const [entryDate, setEntryDate] = useState(todayYmd());
  const [entryUntil, setEntryUntil] = useState("");
  const [entryItemId, setEntryItemId] = useState(null);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [attachPackageId, setAttachPackageId] = useState(null);
  const [attachQuantity, setAttachQuantity] = useState("1");
  const [attachGroupId, setAttachGroupId] = useState(null);
  const [attachGroupQuantity, setAttachGroupQuantity] = useState("1");
  const [periodEdit, setPeriodEdit] = useState(null); // { old_from, old_until, new_from, new_until }
  const [cascadeModal, setCascadeModal] = useState({ show: false, entry: null });
  const [simulatorPackageId, setSimulatorPackageId] = useState(null);
  const [simulatorDate, setSimulatorDate] = useState(todayYmd());
  const [view, setView] = useState("gestion"); // 'gestion' | 'carte'

  useEffect(() => {
    if (!slotsState?.isLoading && !slotsState?.isInitialized) dispatch(getRotationSlots());
  }, [dispatch, slotsState]);
  useEffect(() => {
    if (!packagesState?.isLoading && !packagesState?.isInitialized) dispatch(getProductPackages());
  }, [dispatch, packagesState]);
  useEffect(() => {
    if (!productGroupsState?.isLoading && !productGroupsState?.isInitialized) dispatch(getProductGroups());
  }, [dispatch, productGroupsState]);
  useEffect(() => {
    if (!productsState?.isLoading && !productsState?.isInitialized) dispatch(getProducts());
  }, [dispatch, productsState]);
  useEffect(() => {
    if (!productPartsState?.isLoading && !productPartsState?.isInitialized) dispatch(getProductParts());
  }, [dispatch, productPartsState]);

  const selectedSlot = slots.find((s) => s.id === selectedSlotId) || null;

  const refresh = () => {
    dispatch(getRotationSlots());
    dispatch(getProductPackages());
    dispatch(getProductGroups());
  };

  const handleWrite = async (action, successMessage) => {
    const resp = await dispatch(action);
    if (!resp || resp.error || resp.success === false) {
      toast.error(resp?.message || "Erreur lors de l'enregistrement");
      return null;
    }
    if (successMessage) toast.success(successMessage);
    refresh();
    return resp;
  };

  const handleCreateSlot = async () => {
    if (!newSlotName.trim()) return;
    const resp = await handleWrite(
      postRotationSlot({ name: newSlotName.trim(), item_type: newSlotType }),
      "Slot créé"
    );
    if (resp?.id) {
      setSelectedSlotId(resp.id);
      setNewSlotName("");
    }
  };

  const handleRenameSlot = (slot) => {
    swal({
      title: "Renommer le slot",
      content: { element: "input", attributes: { defaultValue: slot.name } },
      buttons: ["Annuler", "Renommer"],
    }).then((value) => {
      if (value && String(value).trim()) {
        handleWrite(putRotationSlot(slot.id, { name: String(value).trim() }), "Slot renommé");
      }
    });
  };

  const handleDeleteSlot = (slot) => {
    swal({
      title: "Supprimer ce slot ?",
      text: `« ${slot.name} »\n${(slot.package_lines || []).length} forfait(s) rattaché(s). Les devis existants ne sont pas modifiés.`,
      icon: "warning",
      buttons: ["Annuler", "Supprimer"],
      dangerMode: true,
    }).then((confirmed) => {
      if (!confirmed) return;
      handleWrite(deleteRotationSlot(slot.id), "Slot supprimé");
      if (selectedSlotId === slot.id) setSelectedSlotId(null);
    });
  };

  const handleSaveEntry = async () => {
    if (!selectedSlot || !entryItemId || !entryDate) return;
    const body = { effective_from: entryDate, effective_until: entryUntil || null, item_id: entryItemId };
    const resp = editingEntryId
      ? await handleWrite(putRotationSlotEntry(editingEntryId, body), "Rotation mise à jour")
      : await handleWrite(postRotationSlotEntry(selectedSlot.id, body), "Rotation ajoutée");
    if (resp) {
      // Proposer la synchronisation des devis existants pour la fenêtre modifiée
      const savedEntry = (resp.entries || []).find(
        (e) => e.effective_from === entryDate && Number(e.item_id) === Number(entryItemId)
      );
      setEditingEntryId(null);
      setEntryItemId(null);
      setEntryUntil("");
      if (savedEntry) setCascadeModal({ show: true, entry: savedEntry });
    }
  };

  const handleDeleteEntry = (entry) => {
    swal({
      title: "Supprimer cette entrée ?",
      text: `${entry.name} à partir du ${frDate(entry.effective_from)}\n\nLes devis déjà mis à jour par une cascade ne seront pas remis en arrière.`,
      icon: "warning",
      buttons: ["Annuler", "Supprimer"],
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) handleWrite(deleteRotationSlotEntry(entry.id), "Entrée supprimée");
    });
  };

  const handleAttachPackage = async () => {
    if (!selectedSlot || !attachPackageId) return;
    const quantity = parseFloat(String(attachQuantity).replace(",", ".")) || 1;
    const resp = await handleWrite(
      putRotationSlotPackage(selectedSlot.id, { package_id: attachPackageId, quantity }),
      "Forfait rattaché"
    );
    if (resp) {
      setAttachPackageId(null);
      setAttachQuantity("1");
    }
  };

  const handleDetachPackage = (line) => {
    swal({
      title: "Détacher ce forfait ?",
      text: `${line.package_name}\nLe forfait n'aura plus cette ligne tournante.`,
      icon: "warning",
      buttons: ["Annuler", "Détacher"],
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) handleWrite(deleteRotationSlotPackage(selectedSlot.id, line.package_id), "Forfait détaché");
    });
  };

  const handleAttachGroup = async () => {
    if (!selectedSlot || !attachGroupId) return;
    const quantity = parseFloat(String(attachGroupQuantity).replace(",", ".")) || 1;
    const resp = await handleWrite(
      putRotationSlotGroup(selectedSlot.id, { group_id: attachGroupId, quantity }),
      "Groupe rattaché"
    );
    if (resp) {
      setAttachGroupId(null);
      setAttachGroupQuantity("1");
    }
  };

  const handleDetachGroup = (line) => {
    swal({
      title: "Détacher ce groupe ?",
      text: `${line.group_name}\nLe groupe n'aura plus cette ligne tournante.`,
      icon: "warning",
      buttons: ["Annuler", "Détacher"],
      dangerMode: true,
    }).then((confirmed) => {
      if (confirmed) handleWrite(deleteRotationSlotGroup(selectedSlot.id, line.group_id), "Groupe détaché");
    });
  };

  // Mise à jour globale d'une période (tous slots) — utilisée par la carte
  // « Périodes de rotation » (gestion) et par les chips de la Vue d'ensemble.
  const applyPeriodUpdate = async (body) => {
    const resp = await handleWrite(
      updateRotationPeriod({
        old_from: body.old_from,
        old_until: body.old_until || null,
        new_from: body.new_from,
        new_until: body.new_until || null,
      }),
      null
    );
    if (resp) {
      toast.success(`Période mise à jour (${resp.updated ?? 0} rotation(s) décalée(s))`);
    }
    return resp;
  };

  const handleUpdatePeriod = async () => {
    if (!periodEdit?.new_from) return;
    const resp = await applyPeriodUpdate(periodEdit);
    if (resp) setPeriodEdit(null);
  };

  const slotOptions = useMemo(
    () =>
      slots.map((slot) => {
        const summary = slotSummary(slot);
        return {
          value: slot.id,
          label: `${slot.name}${summary.currentName ? ` — ${summary.currentName}` : " — (vide)"}`,
        };
      }),
    [slots]
  );

  const itemOptions = useMemo(() => {
    if (!selectedSlot) return [];
    const pool = selectedSlot.item_type === "product_part" ? productParts : products;
    return (pool || [])
      .filter((p) => p.active !== false)
      .map((p) => ({ value: p.id, label: p.name || `#${p.id}` }));
  }, [selectedSlot, products, productParts]);

  const packageOptions = useMemo(
    () =>
      [...packages]
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .map((p) => ({ value: p.id, label: `${p.name || `#${p.id}`}` })),
    [packages]
  );

  const groupOptions = useMemo(
    () =>
      [...productGroups]
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .map((g) => ({ value: g.id, label: `${g.name || `#${g.id}`}` })),
    [productGroups]
  );

  // Périodes de rotation distinctes (couples début / fin) tous slots confondus,
  // pour la mise à jour globale d'une période (ex : prolonger la carte d'été).
  const periods = useMemo(() => {
    const map = new Map();
    slots.forEach((slot) => {
      (slot.entries || []).forEach((e) => {
        if (!e.effective_from) return;
        const key = `${e.effective_from}|${e.effective_until || ""}`;
        if (!map.has(key)) {
          map.set(key, { from: e.effective_from, until: e.effective_until || null, count: 0 });
        }
        map.get(key).count += 1;
      });
    });
    return [...map.values()].sort((a, b) => String(b.from).localeCompare(String(a.from)));
  }, [slots]);

  const sortedEntries = useMemo(() => {
    if (!selectedSlot) return [];
    return [...(selectedSlot.entries || [])].sort((a, b) =>
      String(b.effective_from || "").localeCompare(String(a.effective_from || ""))
    );
  }, [selectedSlot]);

  // Aperçu du forfait sélectionné dans la zone « Forfaits rattachés » :
  // composition actuelle + repérage des lignes fixes qui doublonneraient avec le slot
  const attachPreview = packages.find((p) => p.id === attachPackageId) || null;
  const slotItemIds = useMemo(() => {
    const ids = new Set();
    (selectedSlot?.entries || []).forEach((e) => {
      if (e.item_id != null) ids.add(Number(e.item_id));
    });
    return ids;
  }, [selectedSlot]);

  const simulatorPackage = packages.find((p) => p.id === simulatorPackageId) || null;
  const simulation = useMemo(() => {
    if (!simulatorPackage) return null;
    return resolvePackageForDeliveryDate(simulatorPackage, simulatorDate, slots);
  }, [simulatorPackage, simulatorDate, slots]);

  const todaysEntryId = selectedSlot ? resolveSlotEntryOn(selectedSlot, todayYmd())?.id ?? null : null;

  return (
    <Layout>
      <div style={{ padding: "15px" }}>
        <AlphaBanner feature="Rotation de la carte" />
        <h4 className="mb-3">
          <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" />
          Rotation de la carte — slots
          <HelpTip title="Comment ça marche ?" placement="right">
            <p className="mb-1">
              Un <strong>slot</strong> est un emplacement tournant partagé entre forfaits
              (ex : « Pièce cocktail — Fruité », « Quiche végé »). Son <strong>calendrier</strong> dit
              quel produit le remplit selon la date : « à partir du 01/06 → Mini cheesecake »,
              « à partir du 12/10 → Mini tatin ».
            </p>
            <p className="mb-1">
              Un forfait garde ses lignes fixes et peut porter des lignes de slot. Quand un
              forfait est ajouté à un devis, chaque slot est remplacé par le <strong>produit
              concret du calendrier à la date de livraison</strong> — le devis, le PDF et les
              fiches de production ne voient que des produits réels.
            </p>
            <p className="mb-1">
              Une saisie de calendrier sert donc <strong>tous les forfaits rattachés</strong> :
              un changement de carte = une entrée par slot à la même date, et la saisonnalité
              ne demande aucune duplication de forfait.
            </p>
            <p className="mb-0">
              <a href={NOTICE_URL} target="_blank" rel="noopener noreferrer">
                📖 Notice complète (guide utilisateur)
              </a>
            </p>
          </HelpTip>
          <span className="float-right">
            <Button
              size="sm"
              variant="outline-secondary"
              className="mr-3"
              as="a"
              href={NOTICE_URL}
              target="_blank"
              rel="noopener noreferrer"
              title="Ouvrir la notice (guide utilisateur)"
            >
              <FontAwesomeIcon icon={faBookOpen} className="mr-1" /> Notice
            </Button>
            <Button
              size="sm"
              variant={view === "gestion" ? "primary" : "outline-primary"}
              className="mr-1"
              onClick={() => setView("gestion")}
            >
              Gestion
            </Button>
            <Button
              size="sm"
              variant={view === "carte" ? "primary" : "outline-primary"}
              className="mr-1"
              onClick={() => setView("carte")}
            >
              Vue d'ensemble
            </Button>
            <Button
              size="sm"
              variant={view === "forfaits" ? "primary" : "outline-primary"}
              className="mr-1"
              onClick={() => setView("forfaits")}
            >
              Carte à une date
            </Button>
            <Button
              size="sm"
              variant={view === "preparation" ? "primary" : "outline-primary"}
              onClick={() => setView("preparation")}
            >
              Préparation de carte
            </Button>
          </span>
        </h4>

        {view === "carte" && (
          <CarteOverview
            slots={slots}
            products={products}
            productParts={productParts}
            packages={packages}
            productGroups={productGroups}
            onSelectSlot={(slotId) => {
              setSelectedSlotId(slotId);
              setView("gestion");
            }}
            onUpdateEntry={(entryId, body) => handleWrite(putRotationSlotEntry(entryId, body), "Rotation mise à jour")}
            onCreateEntry={(slotId, body) => handleWrite(postRotationSlotEntry(slotId, body), "Rotation créée")}
            onUpdatePeriod={applyPeriodUpdate}
          />
        )}

        {view === "forfaits" && (
          <ForfaitsAtDate
            packages={packages}
            slots={slots}
            productGroups={productGroups}
            products={products}
            productParts={productParts}
          />
        )}

        {view === "preparation" && (
          <PreparationCarte
            slots={slots}
            onSelectSlot={(slotId) => {
              setSelectedSlotId(slotId);
              setView("gestion");
            }}
            onCascade={(entry, slot) => setCascadeModal({ show: true, entry, slotName: slot.name })}
          />
        )}

        {view === "gestion" && (
        <Row>
          <Col lg={4}>
            <Card className="mb-3">
              <Card.Header>
                <strong>Slots</strong>
                <HelpTip title="Créer un slot" placement="right">
                  <p className="mb-1">
                    Créez un slot par <strong>rôle</strong> de votre calendrier de rotation (une
                    colonne du Google Sheet), pas par produit : « Cocktail sucré — Fruité »,
                    « Viennoiserie 1 »…
                  </p>
                  <p className="mb-0">
                    Le type (<strong>produit</strong> ou <strong>part de produit</strong>, ex :
                    quiche à la bouchée) détermine ce que le calendrier pourra contenir ; il ne
                    peut plus changer une fois le calendrier commencé.
                  </p>
                </HelpTip>
              </Card.Header>
              <Card.Body>
                <Select
                  options={slotOptions}
                  value={slotOptions.find((o) => o.value === selectedSlotId) || null}
                  onChange={(o) => setSelectedSlotId(o ? o.value : null)}
                  placeholder="Choisir un slot…"
                  isClearable
                  isLoading={slotsState?.isLoading}
                  noOptionsMessage={() => "Aucun slot"}
                />
                <hr />
                <Form.Label className="font-weight-bold mb-1">Nouveau slot</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  className="mb-2"
                  placeholder='Ex : "Pièce cocktail — Fruité", "Viennoiserie 1"…'
                  value={newSlotName}
                  onChange={(e) => setNewSlotName(e.target.value)}
                />
                <div className="mb-2">
                  <Form.Check
                    inline
                    type="radio"
                    id="slot-type-product"
                    label="Produit"
                    checked={newSlotType === "product"}
                    onChange={() => setNewSlotType("product")}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="slot-type-part"
                    label="Part de produit"
                    checked={newSlotType === "product_part"}
                    onChange={() => setNewSlotType("product_part")}
                  />
                </div>
                <Button size="sm" variant="primary" onClick={handleCreateSlot} disabled={!newSlotName.trim()}>
                  <FontAwesomeIcon icon={faPlus} className="mr-1" /> Créer le slot
                </Button>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>
                <strong>Périodes de rotation</strong>
                <HelpTip title="Mise à jour globale d'une période" placement="right">
                  <p className="mb-1">
                    Une <strong>période</strong> = un couple début / fin partagé par les rotations
                    de plusieurs slots (une « carte » : Hiver, Été, Automne…). Modifier une période
                    ici décale <strong>d'un coup</strong> toutes les rotations qui portent exactement
                    ces dates, sur tous les slots.
                  </p>
                  <p className="mb-0">
                    Pour prolonger un <strong>seul</strong> slot, modifiez plutôt la date de fin de
                    sa rotation dans le calendrier du slot.
                  </p>
                </HelpTip>
              </Card.Header>
              <Card.Body>
                {periods.length === 0 ? (
                  <p className="text-muted mb-0">Aucune rotation datée.</p>
                ) : (
                  <Table size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Du</th>
                        <th>Jusqu'au</th>
                        <th className="text-center">Rotations</th>
                        <th style={{ width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {periods.map((p) => {
                        const isEditing =
                          periodEdit &&
                          periodEdit.old_from === p.from &&
                          (periodEdit.old_until || null) === (p.until || null);
                        return (
                          <React.Fragment key={`${p.from}|${p.until || ""}`}>
                            <tr>
                              <td>{frDate(p.from)}</td>
                              <td>{p.until ? frDate(p.until) : <span className="text-muted">ouverte</span>}</td>
                              <td className="text-center">{p.count}</td>
                              <td className="text-right">
                                <Button
                                  size="sm"
                                  variant={isEditing ? "secondary" : "outline-secondary"}
                                  title="Modifier cette période pour tous les slots"
                                  onClick={() =>
                                    setPeriodEdit(
                                      isEditing
                                        ? null
                                        : { old_from: p.from, old_until: p.until, new_from: p.from, new_until: p.until || "" }
                                    )
                                  }
                                >
                                  <FontAwesomeIcon icon={faPen} />
                                </Button>
                              </td>
                            </tr>
                            {isEditing && (
                              <tr>
                                <td colSpan={4} className="bg-light">
                                  <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                                    <span className="text-muted">Nouvelles dates :</span>
                                    <Form.Control
                                      size="sm"
                                      type="date"
                                      style={{ maxWidth: 160 }}
                                      value={periodEdit.new_from}
                                      onChange={(e) => setPeriodEdit({ ...periodEdit, new_from: e.target.value })}
                                    />
                                    <span>→</span>
                                    <Form.Control
                                      size="sm"
                                      type="date"
                                      style={{ maxWidth: 160 }}
                                      value={periodEdit.new_until || ""}
                                      onChange={(e) => setPeriodEdit({ ...periodEdit, new_until: e.target.value })}
                                    />
                                    <Button size="sm" variant="primary" onClick={handleUpdatePeriod} disabled={!periodEdit.new_from}>
                                      Appliquer à tous les slots
                                    </Button>
                                  </div>
                                  <small className="text-muted">
                                    Décale les {p.count} rotation(s) portant exactement {frDate(p.from)} →{" "}
                                    {p.until ? frDate(p.until) : "ouverte"}. Les devis existants ne sont pas modifiés
                                    (utilisez la cascade 🔄 slot par slot si besoin).
                                  </small>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>
                <strong>Simulateur forfait</strong>
                <HelpTip title="Le simulateur" placement="right">
                  <p className="mb-1">
                    Affiche la composition qu'obtiendra un devis si on y ajoute ce forfait pour
                    cette <strong>date de livraison</strong> : lignes fixes + slots résolus par
                    leur calendrier (en <span className="text-primary">bleu</span> avec le badge
                    du slot).
                  </p>
                  <p className="mb-0">
                    Un ⚠ signale un slot sans entrée valable à cette date : à l'ajout au devis,
                    la ligne serait omise et un avertissement s'afficherait. Purement informatif,
                    rien n'est modifié.
                  </p>
                </HelpTip>
              </Card.Header>
              <Card.Body>
                <Select
                  options={packageOptions}
                  value={packageOptions.find((o) => o.value === simulatorPackageId) || null}
                  onChange={(o) => setSimulatorPackageId(o ? o.value : null)}
                  placeholder="Choisir un forfait…"
                  isClearable
                  className="mb-2"
                />
                <Form.Control
                  type="date"
                  value={simulatorDate}
                  onChange={(e) => setSimulatorDate(e.target.value)}
                  style={{ maxWidth: 200 }}
                  className="mb-2"
                />
                {simulation && (
                  <>
                    {(simulation._unresolvedSlots || []).map((u) => (
                      <div key={u.slot_id} className="text-danger small">
                        ⚠ {u.slot_name} : non résolu à cette date
                      </div>
                    ))}
                    <ul className="list-unstyled mb-0 small">
                      {(simulation.products || []).map((line, i) => (
                        <li key={`p_${i}`} className={line._slot_name ? "text-primary" : ""}>
                          {line.quantity != null ? `${line.quantity} × ` : ""}
                          {line.name || `#${line.product_id}`}
                          {line._slot_name && <Badge variant="info" className="ml-1">{line._slot_name}</Badge>}
                          {RESOLUTION_MODE_LABELS[line._resolution_mode] && (
                            <Badge variant="secondary" className="ml-1">{RESOLUTION_MODE_LABELS[line._resolution_mode]}</Badge>
                          )}
                        </li>
                      ))}
                      {(simulation.product_parts || []).map((line, i) => (
                        <li key={`pp_${i}`} className={line._slot_name ? "text-primary" : ""}>
                          {line.quantity != null ? `${line.quantity} × ` : ""}
                          {line.name || `#${line.product_part_id}`}
                          {line._slot_name && <Badge variant="info" className="ml-1">{line._slot_name}</Badge>}
                          {RESOLUTION_MODE_LABELS[line._resolution_mode] && (
                            <Badge variant="secondary" className="ml-1">{RESOLUTION_MODE_LABELS[line._resolution_mode]}</Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8}>
            {!selectedSlot && (
              <EmptyState
                icon={<FontAwesomeIcon icon={faCalendarAlt} />}
                title="Choisissez ou créez un slot"
                hint="Un slot est un emplacement tournant partagé entre forfaits (ex : « Pièce cocktail — Fruité »). Son calendrier détermine le produit concret selon la date de livraison ; une saisie sert tous les forfaits rattachés."
              />
            )}

            {selectedSlot && (
              <>
                <Card className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>
                      <strong>{selectedSlot.name}</strong>{" "}
                      <Badge variant="secondary">
                        {selectedSlot.item_type === "product_part" ? "Part de produit" : "Produit"}
                      </Badge>
                    </span>
                    <span>
                      <Button size="sm" variant="outline-secondary" className="mr-1" title="Renommer" onClick={() => handleRenameSlot(selectedSlot)}>
                        <FontAwesomeIcon icon={faPen} />
                      </Button>
                      <Button size="sm" variant="outline-danger" title="Supprimer" onClick={() => handleDeleteSlot(selectedSlot)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </span>
                  </Card.Header>
                  <Card.Body>
                    <h6 className="mb-2">
                      Calendrier
                      <HelpTip title="Le calendrier du slot" placement="right">
                        <p className="mb-1">
                          Chaque entrée dit : « à partir de cette <strong>date de livraison</strong>,
                          le slot vaut ce produit ». Une entrée reste valable jusqu'à la suivante —
                          inutile de saisir une fin.
                        </p>
                        <p className="mb-1">
                          Les devis créés <strong>après</strong> la saisie se résolvent tout seuls à
                          l'ajout du forfait. Pour les devis <strong>déjà créés</strong> dont la
                          livraison tombe dans la fenêtre, utilisez 🔄 « Synchroniser » : un aperçu
                          liste les devis avant toute modification (remplacement de l'item précédent,
                          re-calcul des prix, re-génération VosFactures).
                        </p>
                        <p className="mb-0">
                          Supprimer ou modifier une entrée ne remet pas en arrière les devis déjà
                          synchronisés.
                        </p>
                      </HelpTip>
                    </h6>
                    <Row className="align-items-end mb-3">
                      <Col md={2}>
                        <Form.Label className="mb-1 small font-weight-bold">Du</Form.Label>
                        <Form.Control
                          size="sm"
                          type="date"
                          value={entryDate}
                          onChange={(e) => setEntryDate(e.target.value)}
                        />
                      </Col>
                      <Col md={2}>
                        <Form.Label className="mb-1 small font-weight-bold">Jusqu'au</Form.Label>
                        <Form.Control
                          size="sm"
                          type="date"
                          value={entryUntil}
                          onChange={(e) => setEntryUntil(e.target.value)}
                        />
                      </Col>
                      <Col md={5}>
                        <Form.Label className="mb-1 small font-weight-bold">
                          {selectedSlot.item_type === "product_part" ? "Part de produit" : "Produit"}
                        </Form.Label>
                        <Select
                          options={itemOptions}
                          value={itemOptions.find((o) => o.value === Number(entryItemId)) || null}
                          onChange={(o) => setEntryItemId(o ? o.value : null)}
                          placeholder="Choisir dans le catalogue B2B…"
                          isClearable
                        />
                      </Col>
                      <Col md={3}>
                        <Button size="sm" variant="primary" onClick={handleSaveEntry} disabled={!entryItemId || !entryDate}>
                          {editingEntryId ? "Enregistrer" : <><FontAwesomeIcon icon={faPlus} className="mr-1" /> Ajouter</>}
                        </Button>
                        {editingEntryId && (
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => { setEditingEntryId(null); setEntryItemId(null); setEntryUntil(""); }}
                          >
                            Annuler
                          </Button>
                        )}
                      </Col>
                    </Row>

                    {sortedEntries.length === 0 ? (
                      <p className="text-muted mb-0">
                        Aucune entrée : le slot ne peut pas être résolu (les forfaits porteurs afficheront un avertissement).
                      </p>
                    ) : (
                      <Table striped hover size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Du</th>
                            <th>Jusqu'au</th>
                            <th>Item</th>
                            <th>Dernière cascade</th>
                            <th style={{ width: 120 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedEntries.map((entry) => (
                            <tr key={entry.id}>
                              <td>
                                {frDate(entry.effective_from)}{" "}
                                {entry.id === todaysEntryId && <Badge variant="success">Aujourd'hui</Badge>}
                              </td>
                              <td>
                                {entry.effective_until ? (
                                  frDate(entry.effective_until)
                                ) : (
                                  <span className="text-muted" title="Jusqu'à la rotation suivante">—</span>
                                )}
                              </td>
                              <td>{entry.name || <span className="text-danger">item supprimé</span>}</td>
                              <td>
                                {entry.last_cascade_at ? (
                                  <small>{frDate(entry.last_cascade_at)} — {entry.last_cascade_orders} devis</small>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="text-right text-nowrap">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="mr-1"
                                  title="Synchroniser les devis existants"
                                  onClick={() => setCascadeModal({ show: true, entry })}
                                >
                                  <FontAwesomeIcon icon={faSyncAlt} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  className="mr-1"
                                  title="Modifier"
                                  onClick={() => {
                                    setEditingEntryId(entry.id);
                                    setEntryDate(entry.effective_from);
                                    setEntryUntil(entry.effective_until || "");
                                    setEntryItemId(entry.item_id);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPen} />
                                </Button>
                                <Button size="sm" variant="outline-danger" title="Supprimer" onClick={() => handleDeleteEntry(entry)}>
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Header>
                    <strong>Forfaits rattachés</strong>
                    <HelpTip title="Rattacher des forfaits" placement="right">
                      <p className="mb-1">
                        Chaque forfait rattaché contient « <strong>Qté × ce slot</strong> » en plus
                        de ses lignes fixes (0,5 possible, comme dans la matrice pièces × forfaits).
                        La composition du forfait dans Sonata n'est pas modifiée.
                      </p>
                      <p className="mb-0">
                        Rattacher ou détacher un forfait ne touche pas les devis existants — seuls
                        les prochains ajouts au devis (et les cascades 🔄) sont concernés.
                      </p>
                    </HelpTip>
                  </Card.Header>
                  <Card.Body>
                    <Row className="align-items-end mb-3">
                      <Col md={7}>
                        <Select
                          options={packageOptions}
                          value={packageOptions.find((o) => o.value === attachPackageId) || null}
                          onChange={(o) => setAttachPackageId(o ? o.value : null)}
                          placeholder="Ajouter un forfait…"
                          isClearable
                        />
                      </Col>
                      <Col md={3}>
                        <InputGroup size="sm">
                          <InputGroup.Prepend>
                            <InputGroup.Text>Qté</InputGroup.Text>
                          </InputGroup.Prepend>
                          <Form.Control
                            type="number"
                            min="0.5"
                            step="0.5"
                            value={attachQuantity}
                            onChange={(e) => setAttachQuantity(e.target.value)}
                          />
                        </InputGroup>
                      </Col>
                      <Col md={2}>
                        <Button size="sm" variant="primary" onClick={handleAttachPackage} disabled={!attachPackageId}>
                          Rattacher
                        </Button>
                      </Col>
                    </Row>

                    {attachPreview && (
                      <div className="border rounded bg-light p-2 mb-3" style={{ fontSize: "0.8rem" }}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <strong>{attachPreview.name}</strong>
                          <a
                            href={sonataPackageEditUrl(attachPreview.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Ouvrir la fiche Sonata du forfait"
                          >
                            Fiche Sonata <FontAwesomeIcon icon={faExternalLinkAlt} />
                          </a>
                        </div>
                        <div className="text-muted mb-1">Composition actuelle :</div>
                        <ul className="list-unstyled mb-0">
                          {(attachPreview.products || []).map((line, i) => (
                            <li key={`p_${i}`}>
                              {line.quantity != null ? `${line.quantity} × ` : ""}
                              {line.name || `#${line.product_id}`}
                              {selectedSlot?.item_type === "product" && slotItemIds.has(Number(line.product_id)) && (
                                <Badge variant="warning" className="ml-1">
                                  ⚠ doublon probable avec ce slot — à retirer de la fiche
                                </Badge>
                              )}
                            </li>
                          ))}
                          {(attachPreview.product_parts || []).map((line, i) => (
                            <li key={`pp_${i}`}>
                              {line.quantity != null ? `${line.quantity} × ` : ""}
                              {line.name || `#${line.product_part_id}`}
                              {selectedSlot?.item_type === "product_part" && slotItemIds.has(Number(line.product_part_id)) && (
                                <Badge variant="warning" className="ml-1">
                                  ⚠ doublon probable avec ce slot — à retirer de la fiche
                                </Badge>
                              )}
                            </li>
                          ))}
                          {(attachPreview.slot_lines || []).map((line) => (
                            <li key={`sl_${line.slot_id}`} className="text-primary">
                              {line.quantity} × <Badge variant="info">{line.slot_name}</Badge>
                            </li>
                          ))}
                          {!(attachPreview.products || []).length &&
                            !(attachPreview.product_parts || []).length &&
                            !(attachPreview.slot_lines || []).length && (
                              <li className="text-muted">Aucune composition.</li>
                            )}
                        </ul>
                      </div>
                    )}
                    {(selectedSlot.package_lines || []).length === 0 ? (
                      <p className="text-muted mb-0">Aucun forfait ne porte ce slot.</p>
                    ) : (
                      <Table striped hover size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Forfait</th>
                            <th style={{ width: 100 }}>Quantité</th>
                            <th style={{ width: 60 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedSlot.package_lines || []).map((line) => (
                            <tr key={line.package_id}>
                              <td>
                                {line.package_name || `#${line.package_id}`}
                                <a
                                  href={sonataPackageEditUrl(line.package_id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-2"
                                  title="Ouvrir la fiche Sonata du forfait"
                                >
                                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                                </a>
                              </td>
                              <td>{line.quantity}</td>
                              <td className="text-right">
                                <Button size="sm" variant="outline-danger" title="Détacher" onClick={() => handleDetachPackage(line)}>
                                  <FontAwesomeIcon icon={faTrash} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>

                {selectedSlot.item_type === "product" && (
                  <Card className="mb-3">
                    <Card.Header>
                      <strong>Groupes rattachés</strong> <span className="text-muted">(plateaux-repas)</span>
                      <HelpTip title="Rattacher des groupes" placement="right">
                        <p className="mb-1">
                          Comme pour les forfaits : chaque groupe rattaché contient
                          « <strong>Qté × ce slot</strong> » en plus de ses produits fixes. Le produit
                          concret est résolu à l'ajout du groupe dans un devis, selon la date de
                          livraison.
                        </p>
                        <p className="mb-0">
                          Seuls les slots de type <strong>produit</strong> peuvent composer un groupe
                          (pas de parts dans les plateaux-repas).
                        </p>
                      </HelpTip>
                    </Card.Header>
                    <Card.Body>
                      <Row className="align-items-end mb-3">
                        <Col md={7}>
                          <Select
                            options={groupOptions}
                            value={groupOptions.find((o) => o.value === attachGroupId) || null}
                            onChange={(o) => setAttachGroupId(o ? o.value : null)}
                            placeholder="Ajouter un groupe…"
                            isClearable
                          />
                        </Col>
                        <Col md={3}>
                          <InputGroup size="sm">
                            <InputGroup.Prepend>
                              <InputGroup.Text>Qté</InputGroup.Text>
                            </InputGroup.Prepend>
                            <Form.Control
                              type="number"
                              min="0.5"
                              step="0.5"
                              value={attachGroupQuantity}
                              onChange={(e) => setAttachGroupQuantity(e.target.value)}
                            />
                          </InputGroup>
                        </Col>
                        <Col md={2}>
                          <Button size="sm" variant="primary" onClick={handleAttachGroup} disabled={!attachGroupId}>
                            Rattacher
                          </Button>
                        </Col>
                      </Row>
                      {(selectedSlot.group_lines || []).length === 0 ? (
                        <p className="text-muted mb-0">Aucun groupe ne porte ce slot.</p>
                      ) : (
                        <Table striped hover size="sm" className="mb-0">
                          <thead>
                            <tr>
                              <th>Groupe</th>
                              <th style={{ width: 100 }}>Quantité</th>
                              <th style={{ width: 60 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedSlot.group_lines || []).map((line) => (
                              <tr key={line.group_id}>
                                <td>
                                  {line.group_name || `#${line.group_id}`}
                                  <a
                                    href={sonataGroupEditUrl(line.group_id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-2"
                                    title="Ouvrir la fiche Sonata du groupe"
                                  >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                                  </a>
                                </td>
                                <td>{line.quantity}</td>
                                <td className="text-right">
                                  <Button size="sm" variant="outline-danger" title="Détacher" onClick={() => handleDetachGroup(line)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                )}
              </>
            )}
          </Col>
        </Row>
        )}

        <CascadeModal
          show={cascadeModal.show}
          entry={cascadeModal.entry}
          slotName={cascadeModal.slotName || selectedSlot?.name}
          onHide={() => setCascadeModal({ show: false, entry: null })}
          onApplied={refresh}
        />
      </div>
    </Layout>
  );
};

export default RotationsForfaits;
